import React from "react";
import { Link } from "react-router-dom";
import "./MetricCard.css";

export interface CardMetric {
  /** Short label under the figure, e.g. "Total" or "Sans entreprise". */
  label: string;
  value: number;
  /** Where the figure leads, filters included, e.g. "/coders?withoutEnterprise=true". */
  to: string;
  /** Explains what the filtered page will show; falls back to the label. */
  title?: string;
  /**
   * Draws attention to a figure counting something that needs doing.
   *
   * Applied only while the figure is non-zero: a count of nothing is not a problem, and
   * an orange "0" would raise an alarm about work that does not exist. Decided here
   * rather than at each call site so every card treats it the same way.
   */
  tone?: "warning" | "danger";
}

interface MetricCardProps {
  title: string;
  icon: React.ComponentType;
  metrics: CardMetric[];
}

/**
 * A dashboard card whose figures are the links, not the card.
 *
 * The card itself is inert — no hover effect and nothing to click. Each figure leads to
 * the page it counts, already filtered, so a reader goes straight to the rows behind the
 * number instead of landing on an unfiltered list and narrowing it by hand.
 *
 * Written as a component because the other dashboard cards are moving to the same shape:
 * one place to change how a card reads, rather than the same markup copied per card.
 */
const MetricCard: React.FC<MetricCardProps> = ({ title, icon, metrics }) => {
  const IconComponent = icon;

  return (
    <div className="metric-card">
      <div className="metric-card-icon">
        <IconComponent />
      </div>
      <h3 className="metric-card-title">{title}</h3>
      <div className="metric-card-values">
        {metrics.map((metric) => {
          const toned = metric.tone && metric.value > 0;

          return (
            <Link
              key={metric.label}
              to={metric.to}
              className={`metric${toned ? ` metric-${metric.tone}` : ""}`}
              title={metric.title ?? metric.label}
            >
              <span className="metric-value">{metric.value}</span>
              <span className="metric-label">{metric.label}</span>
            </Link>
          );
        })}
      </div>
    </div>
  );
};

export default MetricCard;
